// /tenant/profile
import { useEffect, useState } from "react";
import api from "@/lib/api";
import TenantShell from "@/components/TenantShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const LANGS = [["en","English"],["es","Español"],["fr","Français"],["de","Deutsch"],["it","Italiano"],["pt","Português"],["zh","中文"],["ja","日本語"],["hi","हिन्दी"],["ar","العربية"]];

export default function TenantProfile() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant/me").then(({ data }) => {
      setData(data);
      const u = data.user || {};
      setForm({
        name: u.name || "",
        phone: u.phone || "",
        employer: u.employer || "",
        monthly_income: u.monthly_income || "",
        employment_type: u.employment_type || "",
        emergency_contact_name: u.emergency_contact_name || "",
        emergency_contact_phone: u.emergency_contact_phone || "",
        emergency_contact_relationship: u.emergency_contact_relationship || "",
        language: u.language || "en",
        home_currency: u.home_currency || "USD",
        unit_system: u.unit_system || "imperial",
      });
    });
  }, []);

  const h = (k, v) => setForm((p) => ({...p, [k]: v}));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.monthly_income === "") payload.monthly_income = null;
      else if (payload.monthly_income !== null) payload.monthly_income = Number(payload.monthly_income);
      await api.patch("/tenant/me", payload);
      toast.success("Profile saved");
      await refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <TenantShell><div className="flex h-64 items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div></TenantShell>;

  return (
    <TenantShell>
      <div className="space-y-6" data-testid="tenant-profile-page">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Profile</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">My profile</h1>
          <p className="mt-1 text-sm text-slate-500">Email is fixed by your invite. To change it, ask your landlord for a new invite.</p>
        </header>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Personal</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div><Label>Email (read only)</Label><Input value={data.user.email} disabled data-testid="tenant-email-readonly" /></div>
            <div><Label>Full name</Label><Input value={form.name} onChange={(e) => h("name", e.target.value)} data-testid="tenant-profile-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => h("phone", e.target.value)} data-testid="tenant-profile-phone" /></div>
            <div><Label>Employer</Label><Input value={form.employer} onChange={(e) => h("employer", e.target.value)} /></div>
            <div><Label>Monthly income</Label><Input type="number" value={form.monthly_income} onChange={(e) => h("monthly_income", e.target.value)} /></div>
            <div>
              <Label>Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => h("employment_type", v)}>
                <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{["W2","Self-employed","Contractor","Retired","Student","Other"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Emergency contact</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={(e) => h("emergency_contact_name", e.target.value)} /></div>
            <div><Label>Phone</Label><Input value={form.emergency_contact_phone} onChange={(e) => h("emergency_contact_phone", e.target.value)} /></div>
            <div><Label>Relationship</Label><Input value={form.emergency_contact_relationship} onChange={(e) => h("emergency_contact_relationship", e.target.value)} /></div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Preferences</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => h("language", v)}>
                <SelectTrigger data-testid="tenant-profile-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Home currency</Label><Input maxLength={3} value={form.home_currency} onChange={(e) => h("home_currency", e.target.value.toUpperCase())} data-testid="tenant-profile-currency" /></div>
            <div>
              <Label>Units</Label>
              <Select value={form.unit_system} onValueChange={(v) => h("unit_system", v)}>
                <SelectTrigger data-testid="tenant-profile-units"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="imperial">Imperial (sqft)</SelectItem>
                  <SelectItem value="metric">Metric (m²)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="tenant-profile-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save changes
          </Button>
        </div>
      </div>
    </TenantShell>
  );
}
